import React from 'react';
import { ResumeData } from '../../../types/resume';
import { Section } from './shared/Section';
import { ContactInfo } from './shared/ContactInfo';
import { SkillsList } from './shared/SkillsList';

export function ModernTemplate({ data }: { data: ResumeData }) {
  const { personalInfo, experience, education, skills } = data;

  if (!personalInfo) return null;

  return (
    <div className="max-w-[21cm] mx-auto bg-white">
      {/* Header with accent background */}
      <header className="bg-blue-600 text-white px-8 py-6">
        <h1 className="text-3xl font-bold mb-2">{personalInfo.fullName}</h1>
        <p className="text-xl mb-4">{personalInfo.title}</p>
        <ContactInfo 
          info={personalInfo} 
          className="text-blue-50" 
          iconClassName="h-4 w-4 text-blue-200"
        />
      </header>

      <main className="p-8">
        {/* Summary */}
        {personalInfo.summary && (
          <Section title="Profile" className="mb-8">
            <p className="text-gray-700 leading-relaxed">{personalInfo.summary}</p>
          </Section>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="col-span-2">
            {experience && experience.length > 0 && (
              <Section title="Experience" className="mb-8">
                <div className="space-y-6">
                  {experience.map((exp) => (
                    <div key={exp.id} className="relative pl-4 border-l-2 border-blue-200">
                      <div className="absolute left-0 top-0 w-2 h-2 bg-blue-600 rounded-full -translate-x-[5px]" />
                      <div className="mb-2">
                        <div className="flex justify-between items-start">
                          <h3 className="font-bold text-lg text-blue-900">{exp.position}</h3>
                          <span className="text-sm text-gray-500">
                            {exp.startDate} - {exp.current ? 'Present' : exp.endDate}
                          </span>
                        </div>
                        <p className="text-blue-600">{exp.company}</p>
                        <p className="text-sm text-gray-500">{exp.location}</p>
                      </div>
                      <p className="text-gray-700 mb-2">{exp.description}</p>
                      {exp.achievements.length > 0 && (
                        <ul className="list-disc list-inside text-gray-700 space-y-1">
                          {exp.achievements.map((achievement, index) => (
                            <li key={index}>{achievement}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {education && education.length > 0 && (
              <Section title="Education">
                <div className="space-y-4">
                  {education.map((edu) => (
                    <div key={edu.id} className="relative pl-4 border-l-2 border-blue-200">
                      <div className="absolute left-0 top-0 w-2 h-2 bg-blue-600 rounded-full -translate-x-[5px]" />
                      <h3 className="font-bold text-blue-900">{edu.school}</h3>
                      <p className="text-gray-700">{edu.degree} in {edu.field}</p>
                      <p className="text-sm text-gray-500">{edu.graduationDate}</p>
                      {edu.gpa && <p className="text-sm text-gray-500">GPA: {edu.gpa}</p>}
                      {edu.achievements.length > 0 && (
                        <ul className="mt-2 list-disc list-inside text-gray-700 space-y-1">
                          {edu.achievements.map((achievement, index) => (
                            <li key={index}>{achievement}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </div>

          {/* Sidebar */}
          <div>
            {skills && skills.length > 0 && (
              <Section title="Skills">
                <SkillsList 
                  skills={skills} 
                  className="text-gray-700"
                  showLevel={true}
                />
              </Section>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}